import { useEffect, useState } from 'react'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import ScrollToTop from '@/components/base/ScrollToTop'
import Breadcrumb from '@/components/base/Breadcrumb'
import { siteInfo } from '@/mocks/siteInfo'

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [formMsg, setFormMsg] = useState('')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)
    const honeypot = (formData.get('company_alt') as string || '').trim()
    if (honeypot) {
      setFormStatus('success')
      setFormMsg('感谢您的留言，我们会尽快回复！')
      return
    }
    setFormStatus('loading')
    try {
      const payload = new URLSearchParams()
      payload.append('name', formData.get('name') as string)
      payload.append('email', formData.get('email') as string)
      payload.append('message', formData.get('message') as string)
      const resp = await fetch('https://readdy.ai/api/form/d9sv9a58o1vn02ekumj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      })
      const text = await resp.text()
      let parsed: any = {}
      try { parsed = JSON.parse(text) } catch { /* parse failed, use empty object */ }
      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success')
        setFormMsg('感谢您的留言，我们会尽快回复！')
        form.reset()
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。'
        setFormStatus('error')
        setFormMsg(msg)
      }
    } catch {
      setFormStatus('error')
      setFormMsg('网络错误，请检查连接后重试。')
      void 0
    }
  }

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://tomleeled.cn/',
    description: siteInfo.description,
    foundingDate: siteInfo.foundingYear,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteInfo.email,
    },
  }

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '色里番精选推荐是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '色里番精选推荐是一个专注于高品质色里番作品分类导航的平台。我们由一群资深爱好者创立，致力于为色里番爱好者提供最专业的内容推荐服务，涵盖热门番剧、新番推荐、经典回顾等多个维度。',
        },
      },
      {
        '@type': 'Question',
        name: '如何快速找到我喜欢的色里番作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过我们的分类导航系统快速定位感兴趣的作品类型。我们提供热门推荐、新番速递、经典回顾、排行榜等多个分类入口，也可以使用标签筛选功能精准找到符合您口味的作品。',
        },
      },
      {
        '@type': 'Question',
        name: '平台的推荐内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们保持每日更新频率，紧跟行业动态。新番资讯会在第一时间推送，月度排行榜于每月初更新，年度精选则在每年年底进行全面盘点。',
        },
      },
      {
        '@type': 'Question',
        name: '推荐的作品是否经过筛选？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，所有推荐作品都经过专业编辑团队的严格筛选和评审。我们建立了完善的品质评估体系，从画风、剧情、制作水准等多维度进行综合评定，确保每部推荐作品都具备较高品质。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系我们？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过邮箱 ${siteInfo.email} 与我们取得联系，也可以使用本页面的在线留言表单。我们的客服团队会在工作时间内尽快回复您的咨询。`,
        },
      },
    ],
  }

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="text-center py-16 md:py-20">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
              关于 {siteInfo.name}
            </h1>
            <p className="text-lg text-foreground-500 max-w-3xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12 max-w-4xl mx-auto">
              <div className="bg-background-100 rounded-xl p-6">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{siteInfo.foundingYear}</div>
                <div className="text-sm text-foreground-500">创立年份</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">{siteInfo.totalWorks}</div>
                <div className="text-sm text-foreground-500">收录作品</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="text-2xl md:text-3xl font-heading font-bold text-secondary-500 mb-1">{siteInfo.teamSize}</div>
                <div className="text-sm text-foreground-500">团队成员</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{siteInfo.monthlyVisitors}</div>
                <div className="text-sm text-foreground-500">月访问量</div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-50">
            <h2 className="text-3xl font-heading font-bold text-foreground-950 text-center mb-4">我们的优势</h2>
            <p className="text-foreground-500 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              专业团队、严格筛选、持续更新，为您提供最优质的色里番内容发现体验。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-[1200px] mx-auto">
              {siteInfo.features.map((feat, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className={`${feat.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100">
            <h2 className="text-3xl font-heading font-bold text-foreground-950 text-center mb-4">发展历程</h2>
            <p className="text-foreground-500 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              从创立之初到成为行业领先平台，每一步都凝聚着团队的付出与热爱。
            </p>
            <div className="max-w-[800px] mx-auto relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              {siteInfo.timeline.map((item, idx) => (
                <div
                  key={idx}
                  className={`relative flex flex-col md:flex-row items-start gap-6 mb-10 ${
                    idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="flex-1 md:text-right md:pr-10">
                    <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                      <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-2">
                        {item.year}
                      </span>
                      <h3 className="font-heading font-semibold text-foreground-950 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center relative z-10">
                    <i className="ri-check-line text-background-50 text-sm"></i>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-50">
            <h2 className="text-3xl font-heading font-bold text-foreground-950 text-center mb-4">用户口碑</h2>
            <p className="text-foreground-500 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              来自真实用户的评价，是对我们最大的肯定和动力。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1100px] mx-auto">
              {siteInfo.testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading font-bold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100">
            <h2 className="text-3xl font-heading font-bold text-foreground-950 text-center mb-4">常见问题</h2>
            <p className="text-foreground-500 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              关于色里番精选推荐的常见问题解答，帮助您更好地使用我们的平台。
            </p>
            <div className="max-w-[800px] mx-auto space-y-4">
              {[
                { q: '色里番精选推荐是什么？', a: '色里番精选推荐是一个专注于高品质色里番作品分类导航的平台。我们由一群资深爱好者创立，致力于为色里番爱好者提供最专业的内容推荐服务，涵盖热门番剧、新番推荐、经典回顾等多个维度。' },
                { q: '如何快速找到我喜欢的色里番作品？', a: '您可以通过我们的分类导航系统快速定位感兴趣的作品类型。我们提供热门推荐、新番速递、经典回顾、排行榜等多个分类入口，也可以使用标签筛选功能精准找到符合您口味的作品。' },
                { q: '平台的推荐内容多久更新一次？', a: '我们保持每日更新频率，紧跟行业动态。新番资讯会在第一时间推送，月度排行榜于每月初更新，年度精选则在每年年底进行全面盘点。' },
                { q: '推荐的作品是否经过筛选？', a: '是的，所有推荐作品都经过专业编辑团队的严格筛选和评审。我们建立了完善的品质评估体系，从画风、剧情、制作水准等多维度进行综合评定。' },
                { q: '如何联系我们？', a: `您可以通过邮箱 ${siteInfo.email} 与我们取得联系，也可以使用页面的在线留言表单。我们的客服团队会在工作时间内尽快回复。` },
              ].map((item, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-foreground-800 hover:text-primary-500 transition-colors list-none">
                    <span>{item.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-500 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-[700px] mx-auto">
              <h2 className="text-3xl font-heading font-bold text-foreground-950 text-center mb-4">联系我们</h2>
              <p className="text-foreground-500 text-center mb-10 max-w-xl mx-auto leading-relaxed">
                有任何问题、建议或合作意向，欢迎随时与我们联系。
              </p>
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="bg-background-100 rounded-xl p-8 space-y-5"
              >
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的姓名"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的邮箱"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您想说的话（最多500字）"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-center text-sm text-green-600">{formMsg}</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-center text-sm text-red-500">{formMsg}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  )
}